/**
 * Điểm CỦA CHÍNH sinh viên trong 1 môn, tách theo từng kỳ đã đăng ký học
 * (chốt 2026-10-01). Điểm đạt được hiển thị THANG 10 qua `ScoreTag`
 * (CLAUDE.md §13); trọng số là % cấu hình nên giữ nguyên %.
 */
import { Alert, Card, Empty, Space, Spin, Table, Tag, Typography } from "antd";
import { type MyCourseGradeTerm, useMyCourseGrades } from "@/api/assessment";
import { ScoreTag, pctTo10 } from "@/components/ScoreTag";

const { Text } = Typography;

interface Row {
  key: string;
  name: string;
  weight_pct: number;
  /** Thang 10. */
  score10: number | null;
}

function termRows(t: MyCourseGradeTerm): Row[] {
  return [
    ...t.components.map((c) => ({
      key: `q-${c.quiz_id}`,
      name: c.quiz_title,
      weight_pct: c.weight_pct,
      score10: c.score_pct == null ? null : pctTo10(c.score_pct),
    })),
    {
      key: "final",
      name: "Thi cuối kỳ",
      weight_pct: t.final_exam.weight_pct,
      score10: t.final_exam.score,
    },
  ];
}

export function MyCourseGradesByTerm({ courseSlug }: { courseSlug: string }) {
  const { data, isLoading, error } = useMyCourseGrades(courseSlug);

  if (isLoading) return <Spin />;
  if (error) return <Alert type="error" showIcon message="Không tải được điểm của bạn." />;
  if (!data || data.terms.length === 0) {
    return <Empty description="Bạn chưa có kỳ học nào của môn này." />;
  }

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: "100%" }}
      data-testid="my-course-grades"
    >
      {data.terms.map((t) => (
        <Card
          key={t.term.id}
          size="small"
          title={
            <Space wrap>
              <span>{t.term.name}</span>
              {t.term.is_current && <Tag color="blue">Kỳ hiện tại</Tag>}
              {t.term.is_past && <Tag>Đã kết thúc</Tag>}
            </Space>
          }
          extra={
            <Space>
              <Text>Điểm tổng kết:</Text>
              <ScoreTag value={t.total_pct == null ? null : pctTo10(t.total_pct)} />
              {t.is_pass === true && <Tag color="green">Đạt</Tag>}
              {t.is_pass === false && <Tag color="red">Chưa đạt</Tag>}
            </Space>
          }
        >
          <Table<Row>
            size="small"
            pagination={false}
            dataSource={termRows(t)}
            columns={[
              { title: "Thành phần", dataIndex: "name" },
              {
                title: "Trọng số",
                dataIndex: "weight_pct",
                width: 110,
                render: (v: number) => `${v}%`,
              },
              {
                title: "Điểm",
                dataIndex: "score10",
                width: 100,
                render: (v: number | null) => <ScoreTag value={v} />,
              },
            ]}
          />
        </Card>
      ))}
    </Space>
  );
}
